import { i18n } from '@/shared/i18n';

import type { CopyGenerationItem, ResponseStyleLevel } from '../model/copy-widget.types';

// Convert BE channel identifier into a user-friendly label for the UI. The
// channels with no case are brand names, deliberately derived rather than
// translated; the default branch also has to pass through the already-humanized
// section headers `buildCopySectionText` is called with.
export function humanizeChannel(channel: string): string {
  switch (channel) {
    case 'plain':
      return i18n.t('contentTest:copyWidget.channel.plain');
    case 'email':
      return i18n.t('contentTest:copyWidget.channel.email');
    default:
      return channel.charAt(0).toUpperCase() + channel.slice(1);
  }
}

// `balanced` shares the default branch with an absent style, which is the label
// the widget has always shown when the payload carries none.
export function humanizeResponseStyle(style: ResponseStyleLevel | undefined): string {
  switch (style) {
    case 'focused':
      return i18n.t('contentTest:copyWidget.responseStyle.focused');
    case 'imaginative':
      return i18n.t('contentTest:copyWidget.responseStyle.imaginative');
    default:
      return i18n.t('contentTest:copyWidget.responseStyle.balanced');
  }
}

/**
 * Builds markdown text for a single channel section with its items.
 * Used for clipboard copy operations (not export/download).
 */
export function buildCopySectionText(channelKey: string, items: CopyGenerationItem[]): string {
  const channelHeader = `# ${humanizeChannel(channelKey)}`;
  const lines: string[] = [channelHeader];

  for (const item of items) {
    switch (item.channel) {
      case 'plain': {
        lines.push(`\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.text')}\n${item.text}`);
        break;
      }
      case 'instagram': {
        lines.push(`\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.caption')}\n${item.body}`);
        if (Array.isArray(item.hashtags) && item.hashtags.length) {
          const tagLine = item.hashtags.map((t) => (t.startsWith('#') ? t : `#${t}`)).join(' ');
          lines.push(`\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.hashtags')}\n${tagLine}`);
        }
        break;
      }
      case 'email': {
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.subjectLine')}\n${item.subject ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.preheader')}\n${item.preheader ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.heroImageCopy')}\n${item.heroImageCopy ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.bodyTitle')}\n${item.bodyTitle ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.bodyCopy')}\n${item.bodyCopy ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.bodyImageCopy')}\n${item.bodyImageCopy ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.primaryCta')}\n${item.primaryCta ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.secondaryCta')}\n${item.secondaryCta ?? ''}`,
        );
        lines.push(
          `\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.footer')}\n${item.footer ?? ''}`,
        );
        break;
      }
      case 'twitter':
      case 'tiktok':
      case 'facebook':
      default: {
        lines.push(`\n${i18n.t('contentTest:copyWidget.copyWidgetUtils.body')}\n${item.body}`);
      }
    }
  }

  return lines.join('\n');
}

// Format date to full, stable timestamp (e.g., “Sep 18, 2:05 PM”)
export function formatDateTimeAbsolute(value?: string | Date): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
}
